import React, { useEffect, useRef, useState } from 'react';
import FacetteDeroulante from './FacetteDeroulante';

// lot AV10 / P3-5 — la barre de la maquette validée : TOUT sur une seule ligne, 56 px de haut
// (contrôles de 40 px + 8 px de marge intérieure de la carte).
// ⛔ Aucun intitulé au-dessus d'un contrôle de la ligne : c'est ce qui permet la ligne unique.
// Le panneau « Filtres » s'ouvre EN SUPERPOSITION, aligné à droite : ⛔ il ne pousse jamais la
// liste vers le bas. Les pastilles vivent SOUS la carte, elles n'entrent pas dans les 56 px.
const Intitule = ({ titre, suffixe }) => (
  <span className="text-[11px] font-semibold tracking-[0.06em] uppercase text-slate-500">
    {titre}
    {suffixe && <span className="normal-case font-normal tracking-normal text-slate-400"> — {suffixe}</span>}
  </span>
);

const Intervalle = ({ min, max, onMin, onMax, idMin, idMax, unite, desactive, infobulle }) => (
  <div className="flex items-center gap-1.5">
    <input
      type="number" value={min} onChange={(e) => onMin(e.target.value)}
      placeholder="min" disabled={desactive} title={infobulle} data-testid={idMin}
      className={`h-10 w-[98px] px-2.5 text-sm tabular-nums border border-slate-200 rounded-[10px] focus:border-[#007BFF] focus:outline-none ${
        desactive ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
    />
    <span className="text-slate-400">–</span>
    <input
      type="number" value={max} onChange={(e) => onMax(e.target.value)}
      placeholder="max" disabled={desactive} title={infobulle} data-testid={idMax}
      className={`h-10 w-[98px] px-2.5 text-sm tabular-nums border border-slate-200 rounded-[10px] focus:border-[#007BFF] focus:outline-none ${
        desactive ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : ''}`}
    />
    <span className="text-sm text-slate-500 whitespace-nowrap">{unite}</span>
  </div>
);

const Compteur = ({ resultats, comptagePret, testid }) => (
  comptagePret
    ? (
      <span className="text-sm text-slate-500 whitespace-nowrap" data-testid={testid}>
        <b className="text-slate-900 tabular-nums font-semibold">{resultats}</b> bien{resultats > 1 ? 's' : ''}
      </span>
    )
    : (
      // 🔴 Aucun nombre tant que le portefeuille n'est pas complet : un nombre juste sur un
      // échantillon partiel est un nombre faux.
      <span className="text-sm text-amber-600 whitespace-nowrap" data-testid="portefeuille-chargement">
        <i className="bi bi-arrow-repeat animate-spin mr-1" />chargement…
      </span>
    )
);

export const FacetSearchBar = ({
  reference,
  onReference,
  facettes,
  onToggle,
  bornes,
  onBorne,
  budgetNature,      // 'vente' | 'location' | null (aucune transaction, ou mélange des deux)
  budgetMelange,     // true = la sélection mélange vente et location (le champ reste grisé)
  regroupements,     // relevé des libellés regroupés (dette de données)
  pastilles,
  nbFiltresActifs,
  resultats,
  comptagePret,
  ouvert,
  setOuvert,
  onReset,
  statutMobile,      // lot CW — <select> de statut, affiché SEULEMENT sur téléphone
  triMobile,         // lot CW — <select> de tri, affiché SEULEMENT sur téléphone
}) => {
  const zoneFiltres = useRef(null);
  // lot CW — sous 768 px, la ligne unique ne tient pas : la recherche prend toute la largeur,
  // les filtres rapides descendent dans le panneau, et le panneau devient une feuille montante.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  // ⛔ Un seul menu ouvert à la fois : ouvrir une liste de la ligne referme le panneau, et
  // inversement. Les listes DANS le panneau, elles, ne le referment pas.
  const [menuOuvert, setMenuOuvert] = useState(null);

  useEffect(() => {
    // Sur téléphone, le panneau est une feuille montante POSÉE HORS de `zoneFiltres` : c'est son
    // fond assombri qui la referme. ⛔ Garder ce guetteur fermerait la feuille au premier clic.
    if (!ouvert || isMobile) return undefined;
    const auClic = (e) => { if (zoneFiltres.current && !zoneFiltres.current.contains(e.target)) setOuvert(false); };
    document.addEventListener('mousedown', auClic);
    return () => document.removeEventListener('mousedown', auClic);
  }, [ouvert, setOuvert, isMobile]);

  const deroulante = (f) => (
    <FacetteDeroulante
      key={f.cle}
      cle={f.cle}
      titre={f.titre}
      vide={f.vide}
      accord={f.accord}
      options={f.options}
      selection={f.selection}
      onToggle={(v) => onToggle(f.cle, v)}
      comptagePret={comptagePret}
      nbValeursPortefeuille={f.nbValeursPortefeuille}
      nomDansBouton={Boolean(f.rapide)}
      // lot CW2 — sur téléphone, les facettes sont DANS la feuille : leur liste s'ouvre dans le
      // flux, sinon elle sort de l'écran avec son propre défilement.
      enLigne={isMobile}
      ouvert={menuOuvert === f.cle}
      onBasculer={() => {
        setMenuOuvert(m => (m === f.cle ? null : f.cle));
        // 🔴 lot CW2 — fermer le panneau sur un filtre rapide n'a de sens que sur ordinateur, où
        // ces filtres sont HORS du panneau. Sur téléphone ils sont DEDANS : toucher « Ville »
        // refermait la feuille au lieu d'ouvrir la liste.
        if (f.rapide && !isMobile) setOuvert(false);
      }}
      onFermer={() => setMenuOuvert(m => (m === f.cle ? null : m))}
    />
  );

  // ⚠️ Une rubrique masquée (une seule valeur dans TOUT le portefeuille, et rien de coché) doit
  // emporter son INTITULÉ, sinon il reste un titre orphelin dans le panneau.
  // 🔴 On regarde le portefeuille, ⛔ jamais le résultat filtré : sinon, plus on filtre, moins
  // on a de filtres.
  const rubriqueUtile = (f) => f.nbValeursPortefeuille > 1 || f.selection.length > 0;

  const budgetTitre = budgetNature === 'vente' ? 'Prix FAI'
    : (budgetNature === 'location' ? 'Loyer annuel' : 'Budget');
  const budgetSuffixe = budgetNature ? null
    : (budgetMelange ? 'une vente et une location ne se comparent pas' : 'choisissez une transaction');

  // Rubriques du panneau. Sur téléphone, les filtres rapides (Ville, Type de bien…) quittent la
  // ligne et viennent EN TÊTE, chacun avec son intitulé : ⛔ ils ne sont jamais rendus deux fois.
  const rubriquesPanneau = (
    <div className="flex flex-wrap gap-x-4 gap-y-3.5 text-left">
      {isMobile && facettes.filter(f => f.rapide).map(f => (
        <div key={f.cle} className="flex flex-col gap-1.5">
          <Intitule titre={f.titre} />
          {deroulante(f)}
        </div>
      ))}
      {facettes.filter(f => !f.rapide && rubriqueUtile(f)).map(f => (
        <div key={f.cle} className="flex flex-col gap-1.5">
          <Intitule titre={f.titre} />
          {deroulante(f)}
        </div>
      ))}

      <div className="flex flex-col gap-1.5">
        <Intitule titre={budgetTitre} suffixe={budgetSuffixe} />
        <Intervalle
          min={bornes.budgetMin} max={bornes.budgetMax}
          onMin={(v) => onBorne('budgetMin', v)} onMax={(v) => onBorne('budgetMax', v)}
          idMin="facette-budgetMin" idMax="facette-budgetMax"
          unite={budgetNature === 'location' ? '€ / an' : '€'}
          desactive={!budgetNature}
          infobulle={budgetNature ? ''
            : (budgetMelange ? 'Une vente et une location ne se comparent pas'
              : 'Choisissez une transaction (vente ou location)')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Intitule titre="Surface" />
        <Intervalle
          min={bornes.surfaceMin} max={bornes.surfaceMax}
          onMin={(v) => onBorne('surfaceMin', v)} onMax={(v) => onBorne('surfaceMax', v)}
          idMin="facette-surfaceMin" idMax="facette-surfaceMax"
          unite="m²"
        />
      </div>
    </div>
  );

  const boutonEffacer = (
    <button type="button" onClick={onReset} data-testid="filter-reset"
      className="text-[13px] text-slate-500 hover:text-slate-700 hover:bg-slate-100 px-2 py-1.5 rounded-lg">
      Tout effacer
    </button>
  );

  // Relevé des libellés regroupés (deux codes, une ligne) — sert à commander la migration de
  // données. Invisible à l'écran, lisible en recette.
  const releveRegroupements = (
    <span className="sr-only" data-testid="facettes-regroupements">{(regroupements || []).join(' | ')}</span>
  );

  const boutonFiltres = (
    <button
      type="button"
      onClick={() => { setMenuOuvert(null); setOuvert(!ouvert); }}
      data-testid="facettes-toggle"
      aria-expanded={ouvert}
      // lot CW2 — sur téléphone, le mot « Filtres » mangeait la place des deux menus, qui
      // s'affichaient coupés : il ne reste que l'icône, dans un carré, avec la pastille en
      // exposant. ⛔ Même testid, même comportement.
      aria-label={isMobile ? (nbFiltresActifs > 0 ? `Filtres, ${nbFiltresActifs} actifs` : 'Filtres') : undefined}
      className={`${isMobile
        ? 'relative shrink-0 w-11 h-[42px] justify-center'
        : 'h-10 px-3'} rounded-[10px] text-sm border flex items-center gap-2 whitespace-nowrap transition-colors ${
        nbFiltresActifs > 0
          ? 'border-[#007BFF] bg-[#EFF6FF] text-[#007BFF] font-medium'
          : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
      }`}
    >
      <i className="bi bi-sliders"></i>
      {!isMobile && 'Filtres'}
      {nbFiltresActifs > 0 && (
        <span className={`min-w-[19px] h-[19px] px-1.5 rounded-full bg-[#0062CC] text-white text-[11.5px] font-semibold inline-flex items-center justify-center ${
          isMobile ? 'absolute -top-1.5 -right-1.5' : ''}`}
              data-testid="facettes-compteur-actifs">
          {nbFiltresActifs}
        </span>
      )}
    </button>
  );

  const panneau = ouvert && (isMobile ? (
    // Feuille montante : à 360 px, un panneau de 640 px posé en absolu sortait de l'écran.
    <>
      <div className="fixed inset-0 z-[59] bg-black/40" onClick={() => setOuvert(false)}></div>
      <div
        data-testid="facettes-panneau"
        className="fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-2xl shadow-[0_-12px_40px_rgba(15,23,42,0.25)] p-4 max-h-[85vh] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto">{rubriquesPanneau}</div>
        <div className="flex items-center justify-between gap-3 mt-3.5 pt-3 border-t border-slate-200">
          {boutonEffacer}
          <button type="button" onClick={() => setOuvert(false)} data-testid="facettes-voir-resultats"
            className="flex-1 h-[42px] rounded-[10px] bg-[#0062CC] text-white text-sm font-semibold">
            Voir les {resultats} bien{resultats > 1 ? 's' : ''}
          </button>
        </div>
        {releveRegroupements}
      </div>
    </>
  ) : (
    <div
      data-testid="facettes-panneau"
      className="absolute z-[60] top-[calc(100%+6px)] right-0 w-[640px] max-w-[88vw] bg-white border border-slate-200 rounded-xl shadow-[0_18px_48px_rgba(15,23,42,0.18)] p-4"
    >
      {rubriquesPanneau}

      <div className="flex items-center justify-between mt-3.5 pt-3 border-t border-slate-200">
        {boutonEffacer}
        <Compteur resultats={resultats} comptagePret={comptagePret} testid="facettes-resultats" />
      </div>

      {releveRegroupements}
    </div>
  ));

  return (
    <div>
      <div className="bg-white border border-slate-200 rounded-xl p-2 shadow-sm" data-testid="facettes-bloc">
        {/* lot DK3 — sous 1 280 px la ligne passe a la ligne, la recherche occupe la premiere */}
        <div className="flex flex-wrap xl:flex-nowrap items-center gap-2.5" ref={zoneFiltres}>
          <div className="basis-full xl:basis-auto flex-1 min-w-0 md:min-w-[180px] flex items-center gap-2 h-10 px-3 bg-white border border-slate-200 rounded-[10px]">
            <i className="bi bi-search text-slate-400 text-sm"></i>
            <input
              type="text"
              value={reference}
              onChange={(e) => onReference(e.target.value)}
              placeholder="Référence, ville, enseigne, mot-clé…"
              data-testid="filter-reference"
              aria-label="Rechercher un bien"
              className="flex-1 h-9 text-sm bg-transparent border-0 outline-none"
            />
          </div>

          {!isMobile && facettes.filter(f => f.rapide).map(f => deroulante(f))}

          {!isMobile && <div className="relative shrink-0">{boutonFiltres}{panneau}</div>}

          {/* Sur téléphone, le nombre de biens est dit sous la barre : ⛔ pas deux fois. */}
          {!isMobile && (
            <Compteur resultats={resultats} comptagePret={comptagePret} testid="filter-results-count" />
          )}
        </div>

        {/* lot CW — ligne 2 (téléphone) : Statut · Filtres · Tri, trois boutons de même hauteur. */}
        {isMobile && (
          <div className="flex items-stretch gap-2 mt-2" data-testid="facettes-ligne-mobile">
            {statutMobile}
            {boutonFiltres}
            {triMobile}
          </div>
        )}
        {isMobile && panneau}
      </div>

      {/* Les pastilles vivent SOUS la carte : elles n'entrent pas dans la hauteur des 56 px. */}
      {pastilles.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="facettes-pastilles">
          {pastilles.map(p => (
            <span key={p.id} data-testid={`facette-pastille-${p.id}`}
              className="inline-flex items-center gap-2 h-[29px] pl-3 pr-1.5 rounded-full bg-[#EFF6FF] text-[#0062CC] text-[13px] border border-blue-200">
              {p.libelle}
              <button type="button" onClick={p.retirer}
                className="w-[18px] h-[18px] rounded-full flex items-center justify-center hover:bg-blue-200"
                aria-label={`Retirer le filtre ${p.libelle}`} title="Retirer ce filtre">
                <i className="bi bi-x text-sm"></i>
              </button>
            </span>
          ))}
          <button type="button" onClick={onReset} data-testid="filter-reset-pastilles"
            className="text-[13px] text-slate-500 hover:text-slate-700 underline px-1.5">
            Tout effacer
          </button>
        </div>
      )}
    </div>
  );
};

export default FacetSearchBar;
